import React from 'react';
import { connect } from 'react-redux';
import TooltipElement from '../components/tooltipElement';
import TooltipButtonGroup from '../components/tooltipButtonGroup';
import WebViewerLicensesTable from '../components/webViewerLicensesTable';
import OverlayLoader from '../components/overlayLoader';
import TableSelectionManager from '../components/tableSelectionManager';
import * as webViewerLicensesActions from '../store/webViewerLicensesSlice';
import { webViewerLicensesSelectors } from '../store/webViewerLicensesSlice';
import * as objectDataActions from '../store/objectDataSlice';
import { objectDataNames, objectDataSelectors } from '../store/objectDataSlice';
import WebViewerLicenseEditDialog from './webViewerLicenseEditDialog';
import WebViewerLicenseHistoryDialog from './webViewerLicenseHistoryDialog';
import RemoveRestoreWebViewerLicensesConfirmDialog from './removeRestoreWebViewerLicensesConfirmDialog';
import setTitleToFilters from '../common/filtersTitles';

import './common.css';
import { userConfigsSelectors } from '../store/userConfigs';

const thatObjectName = 'webViewerLicenses';

class WebViewerLicensesContainer extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showWebViewerLicenseEditDialog: false,
      webViewerLicenseEditDialogMode: WebViewerLicenseEditDialog.Mode.Edit,
      showHistoryDialog: false,
      showRemoveRestoreWebViewerLicensesConfirmDialog: false,
      removeRestoreWebViewerLicensesConfirmDialogMode: null,
    }
  }

  componentDidMount() {
    setTitleToFilters();
    this.props.fetchAllWebViewerLicenses();
    this.props.fetchLivingStates();
  }

  render() {
    return (
      <OverlayLoader active={this.isLoading()} text={this.getLoaderMessage()}>
        <div>
          <h1>Web viewer licenses</h1>
          {this.renderButtons()}
          {this.renderContent()}
          {this.renderWebViewerLicenseEditDialog()}
          {this.renderHistoryDialog()}
          {this.renderRemoveRestoreWebViewerLicensesConfirmDialog()}
        </div>
      </OverlayLoader>
    );
  }

  renderButtons = () => (
    <TooltipButtonGroup>
      <TooltipElement type="button" text="Refresh" icon="refresh" onClick={this.props.fetchAllWebViewerLicenses}/>
      <TooltipElement type="button" text="Edit web viewer license" icon="edit" onClick={this.editWebViewerLicense} disabled={this.isEditButtonDisabled()} displayOnlyForWriter />
      <TooltipElement type="button" text="Remove web viewer license" icon="remove" onClick={this.removeWebViewerLicenses} disabled={this.isRemoveButtonDisabled()} displayOnlyForWriter />
      <TooltipElement type="button" text="Restore web viewer license" icon="repeat" onClick={this.restoreWebViewerLicenses} disabled={this.isRestoreButtonDisabled()} displayOnlyForWriter />
      <TooltipElement type="button" text="Show web viewer license history" icon="list-alt" onClick={this.showWebViewerLicenseHistory} disabled={this.isHistoryButtonDisabled()}/>
    </TooltipButtonGroup>
  );

  getActionsForFavorites = () => ({
    addToFavorites: this.props.addWebViewerLicenseToFavorites,
    removeFromFavorites: this.props.removeWebViewerLicenseFromFavorites,
  });

  renderContent = () => {
    if (this.props.webViewerLicensesFetchingError)
      return this.renderError(this.props.webViewerLicensesFetchingError.toString());
    return (
      <TableSelectionManager
      items={this.props.webViewerLicenses}
      objectName={thatObjectName}
      >
        <WebViewerLicensesTable
          webViewerLicenses={this.props.webViewerLicenses}
          livingStates={this.props.livingStatesState.data}
          actions={this.getActionsForFavorites()}
          requestData={this.props.requestData}
        />
      </TableSelectionManager>
    );
  };

  renderError = (message) => <p className="text-danger">Error: {message}</p>;

  isLoading = () => this.props.isWebViewerLicensesFetching ||
    this.props.livingStatesState.isFetching;

  getLoaderMessage = () => {
    if (this.props.isWebViewerLicensesFetching) {
      return 'Web viewer licenses loading...';
    }
    if (this.props.livingStatesState.isFetching) {
      return 'Web viewer license living states loading...';
    }
  };

  renderWebViewerLicenseEditDialog = () => (
    <WebViewerLicenseEditDialog
      mode={this.state.webViewerLicenseEditDialogMode}
      onClose={this.closeWebViewerLicenseEditDialog}
      show={this.state.showWebViewerLicenseEditDialog}
      webViewerLicenseId={this.getSelectedWebViewerLicenseFieldValue('id')}
      selectedWebViewerFeaturePackages={
        this.getSelectedWebViewerLicenseFieldValue('featurePackages', [])
          .map(featurePackage => featurePackage.id)
      }
    />
  )

  isEditButtonDisabled = () => this.props.selectedWebViewerLicenses.length !== 1;

  editWebViewerLicense = () => {
    this.setState({
      webViewerLicenseEditDialogMode: WebViewerLicenseEditDialog.Mode.Edit,
      showWebViewerLicenseEditDialog: true
    });
  }

  closeWebViewerLicenseEditDialog = () => {
    this.setState({ showWebViewerLicenseEditDialog: false });
  };

  getSelectedWebViewerLicenseFieldValue = (fieldName, defaultValue = null) => {
    const webViewerLicense = this.props.selectedWebViewerLicenses[0];
    if (!webViewerLicense) return defaultValue;
    try {
      const value = fieldName.split('.').reduce((acc, key) => acc[key], webViewerLicense);
      return value === undefined ? defaultValue : value === null ? defaultValue : value;
    } catch (e) {
      return defaultValue;
    }
  };

  renderRemoveRestoreWebViewerLicensesConfirmDialog = () => (
    <RemoveRestoreWebViewerLicensesConfirmDialog
      show={this.state.showRemoveRestoreWebViewerLicensesConfirmDialog}
      onClose={this.closeRemoveRestoreWebViewerLicensesConfirmDialog}
      licenseIds={this.props.selectedWebViewerLicensesIds}
      mode={this.state.removeRestoreWebViewerLicensesConfirmDialogMode}
    />
  );

  isRemoveButtonDisabled = () => {
    const licenses = this.props.selectedWebViewerLicenses;
    return licenses.length === 0 || licenses.find(license => !license.isAlive) !== undefined;
  };

  isRestoreButtonDisabled = () => {
    const licenses = this.props.selectedWebViewerLicenses;
    return licenses.length === 0 || licenses.find(license => license.isAlive) !== undefined;
  };

  removeWebViewerLicenses = () => {
    this.setState({
      removeRestoreWebViewerLicensesConfirmDialogMode: RemoveRestoreWebViewerLicensesConfirmDialog.Mode.Remove,
      showRemoveRestoreWebViewerLicensesConfirmDialog: true,
    });
  };
  
  restoreWebViewerLicenses = () => {
    this.setState({
      removeRestoreWebViewerLicensesConfirmDialogMode: RemoveRestoreWebViewerLicensesConfirmDialog.Mode.Restore,
      showRemoveRestoreWebViewerLicensesConfirmDialog: true,
    });
  };

  closeRemoveRestoreWebViewerLicensesConfirmDialog = () => {
    this.setState({ showRemoveRestoreWebViewerLicensesConfirmDialog: false });
  };

  renderHistoryDialog = () => {
    return (
      <WebViewerLicenseHistoryDialog
        show={this.state.showHistoryDialog}
        onClose={this.closeHistoryDialog}
        id={this.getSelectedWebViewerLicenseFieldValue('id')}
      />
    );
  };

  showWebViewerLicenseHistory = () => {
    this.setState({ showHistoryDialog: true });
  };

  closeHistoryDialog = () => {
    this.setState({ showHistoryDialog: false });
  };

  isHistoryButtonDisabled = () => this.props.selectedWebViewerLicenses.length !== 1;
}

const mapStateToProps = (state) => ({
  isWebViewerLicensesFetching: webViewerLicensesSelectors.isFetching(state),
  webViewerLicenses: webViewerLicensesSelectors.selectAll(state),
  webViewerLicensesFetchingError: webViewerLicensesSelectors.fetchingError(state),

  livingStatesState: objectDataSelectors.webViewerLicenseLivingState(state),

  selectedWebViewerLicensesIds: userConfigsSelectors.selectedItems(state, thatObjectName),
  selectedWebViewerLicenses: webViewerLicensesSelectors.selectedWebViewerLicenses(state),
});

const actionCreators = {
  fetchAllWebViewerLicenses: webViewerLicensesActions.fetchAllWebViewerLicenses,
  addWebViewerLicenseToFavorites: webViewerLicensesActions.addToFavorites,
  removeWebViewerLicenseFromFavorites: webViewerLicensesActions.removeFromFavorites,
  fetchLivingStates: () => objectDataActions.fetchObjectData({ objectName: objectDataNames.webViewerLicenseLivingStates }),
};

export default connect(mapStateToProps, actionCreators)(WebViewerLicensesContainer);


// WEBPACK FOOTER //
// ./src/containers/webViewerLicensesContainer.js